import { useEffect, useRef } from "react";
import { runAsBackground } from "./api";

/** Focus refetching is what makes the app feel live; this interval is only a
 *  safety net for a tab left open and untouched, so it stays deliberately slow
 *  to keep well inside the Workers/D1 free-tier daily allowances. */
const DEFAULT_INTERVAL = 60_000;

/**
 * Keeps a screen's data current across users: refetches when the tab regains
 * focus and on a quiet interval while it stays visible. Polling stops entirely
 * when the tab is hidden so background tabs cost nothing.
 */
export function useLiveData(refresh: () => void | Promise<unknown>, options: { intervalMs?: number; enabled?: boolean } = {}): void {
  const { intervalMs = DEFAULT_INTERVAL, enabled = true } = options;
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    let running = false;

    const run = () => {
      if (running || document.hidden) return;
      running = true;
      // Flagged as background so it refreshes data without extending the session.
      Promise.resolve(runAsBackground(() => latest.current())).catch(() => undefined).finally(() => { running = false; });
    };
    const start = () => { window.clearInterval(timer); timer = window.setInterval(run, intervalMs); };
    const onVisibility = () => { if (document.hidden) window.clearInterval(timer); else { run(); start(); } };

    if (!document.hidden) start();
    window.addEventListener("focus", run);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", run); document.removeEventListener("visibilitychange", onVisibility); };
  }, [intervalMs, enabled]);
}
